import { StandardProps } from "@/types/types";
import { Facet } from "@/types/productListing";
import styles from "./HorizontalFilters.module.scss";
import classNames from "classnames/bind";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { FilterOutlined as Filter24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/FilterOutlined";
import { useState } from "react";
import { FilterDrawer } from "../__shared__/FilterDrawer/FilterDrawer";
import { FilterButtonWithDropdown } from "./components/FilterButtonWithDropdown/FilterButtonWithDropdown";
import { sendMonetateEvent } from "@/utils/sendMonetateEvent";
import { useRouteParams } from "@/hooks/useRouteParams";
import { getFacetsWithSelectedValues } from "@/utils/setJLData";
import { createDispatchFacetAnalytics } from "../Facets/utils/createDispatchFacetAnalytics";

const cx = classNames.bind(styles);

// Mandatory filters that should be excluded from the top 5
const EXCLUDED_DIMENSIONS = ["childSKUs.hideOutOfStock", "sku.offerType"];

type HorizontalFiltersProps = Pick<
  StandardProps,
  "productListingData" | "configProps"
>;

/**
 * Gets the first 5 dynamic filters (excluding mandatory filters)
 * @param facets - All facets from product listing data
 * @returns Array of up to 5 non-mandatory facets
 */
export function getTop5Filters(facets: Facet[]): Facet[] {
  const dynamicFacets = facets.filter(
    (facet) => !EXCLUDED_DIMENSIONS.includes(facet.dimensionName),
  );
  return dynamicFacets.slice(0, 5);
}

export function HorizontalFilters({
  productListingData,
  configProps,
}: HorizontalFiltersProps) {
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { facets } = productListingData;
  const { pageType } = useRouteParams();

  const top5Filters = getTop5Filters(facets);

  const handleToggleDropdown = (index: number) => {
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  const handleCloseDropdown = () => {
    setOpenDropdownIndex(null);
  };

  const handleAllFilters = () => {
    sendMonetateEvent("plp.allFilters.interaction");
    setDrawerOpen(true);
  };

  const dispatchFacetAnalytics = createDispatchFacetAnalytics({
    pageType,
    results: productListingData.results,
    getSelectedFacets: () =>
      getFacetsWithSelectedValues(productListingData.facets),
  });

  return (
    <div
      className={cx("horizontalFilters", {
        "horizontalFilters--desktopOnly": !!configProps?.desktopOnly,
      })}
      data-component="HorizontalFilters"
    >
      <div className={styles.filtersRow}>
        {top5Filters.map((facet, index) => (
          <FilterButtonWithDropdown
            key={facet.dimensionName}
            facet={facet}
            isOpen={openDropdownIndex === index}
            onToggle={() => handleToggleDropdown(index)}
            onClose={handleCloseDropdown}
            dispatchFacetAnalytics={dispatchFacetAnalytics}
          />
        ))}
      </div>

      <TertiaryButton
        data-testid="all-filters-button"
        className={styles.allFiltersButton}
        onClick={handleAllFilters}
        IconComponent={Filter24PxOutlinedIcon}
        small
        variant="tonal-dark"
      >
        All Filters
      </TertiaryButton>

      <FilterDrawer
        isOpen={drawerOpen}
        standardProps={{
          configProps,
          productListingData: productListingData,
        }}
        handleClose={() => {
          setDrawerOpen(false);
        }}
        position="right"
        targetFacetName={"On Offer"}
      />
    </div>
  );
}
